import { Box, useToken } from '@chakra-ui/react';
import type { OptionBase } from 'chakra-react-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BulbIcon } from '@/assets/icons';
import FaceliftSelectInner from '@/components/Atoms/FaceliftSelect/FaceliftSelectInner';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import { colors } from '@/components/ui/theme';
import { Tooltip } from '@/components/ui/tooltip';
import { debounce } from '@/utils';
import type { FaceliftSelectProps, SelectItem } from './FaceliftSelect.types';

const FaceliftSelect = <
	Option extends OptionBase = SelectItem,
	IsMulti extends boolean = false,
>({
	selectKey,
	placeholder = 'Select',
	loading,
	required,
	topLabel,
	items = [],
	color = 'brand.inputText',
	error = '',
	isMulti,
	ai = false,
	rounded = false,
	limit = 10,
	onLoadMore,
	aiSuggestionText = 'Try searching by: "Smith"',
	...rest
}: FaceliftSelectProps<Option, IsMulti>) => {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const previousSearchRef = useRef<string>('');

	const debouncedSearch = debounce((query: string) => {
		// Only reset page if search actually changed
		if (query !== previousSearchRef.current) {
			setPage(1);
			previousSearchRef.current = query;
		}
		setSearch(query);
	}, 500);

	const onScrollToBottom = () => {
		if (!loading) {
			setPage((prev) => prev + 1);
		}
	};
	
	// Reset previous search ref when selectKey changes
	useEffect(() => {
		previousSearchRef.current = '';
		setPage(1);
		setSearch('');
	}, [selectKey]);

	useEffect(() => {
		onLoadMore?.(page, limit, search);
	}, [page, search, onLoadMore, limit]);

	// Ensure selected value appears at the top of items if it's not in the current list
	const enhancedItems = useMemo(() => {
		const value = rest.value as Option[] | Option | null;
		
		// Add "Select All" option for multi-select when onLoadMore is not available
		const SELECT_ALL_VALUE = '__SELECT_ALL__';
		const shouldShowSelectAll = isMulti && !onLoadMore && items.length > 0;
		
		// Check if all options are selected (for Select All checkbox state)
		// Filter out the Select All option from the value array
		let allSelected = false;
		if (isMulti && Array.isArray(value) && items.length > 0) {
			const realSelectedValues = value.filter(
				(opt) => (opt as any)?.__isSelectAll !== true
			);
			allSelected = realSelectedValues.length > 0 && realSelectedValues.length === items.length;
		}
		
		const selectAllOption = {
			label: 'Select All',
			value: SELECT_ALL_VALUE,
			__isSelectAll: true,
			__allSelected: allSelected,
		} as unknown as Option;
		
		if (!value) {
			return shouldShowSelectAll ? [selectAllOption, ...items] : items;
		}

		// Helper function to get the value from an option
		const getOptionValue = (option: any): string | number | null => {
			if (!option) return null;
			if (typeof option === 'object' && 'value' in option) {
				return option.value;
			}
			return null;
		};

		// Helper function to check if an option exists in items by comparing values
		const optionExistsInItems = (
			optionValue: string | number | null,
			itemsList: Option[],
		): boolean => {
			if (optionValue === null) return false;
			return itemsList.some((item) => {
				const itemValue = getOptionValue(item);
				return itemValue === optionValue;
			});
		};

		// Handle multi-select case
		if (isMulti && Array.isArray(value)) {
			// Filter out the Select All option from the value array
			const realValue = value.filter(
				(opt) => opt && (opt as any)?.__isSelectAll !== true
			);
			
			// If items array is empty, just return the selected values (without Select All)
			if (items.length === 0 && realValue.length > 0) {
				return shouldShowSelectAll 
					? [selectAllOption, ...realValue.filter((opt) => opt) as Option[]]
					: (realValue.filter((opt) => opt) as Option[]);
			}

			const missingOptions: Option[] = [];

			realValue.forEach((selectedOption) => {
				if (!selectedOption) return;

				const optionValue = getOptionValue(selectedOption);
				const exists = optionExistsInItems(optionValue, items);

				if (!exists) {
					// Check if already added to missingOptions
					const alreadyAdded = missingOptions.some(
						(opt) => getOptionValue(opt) === optionValue,
					);
					if (!alreadyAdded && selectedOption) {
						missingOptions.push(selectedOption as Option);
					}
				}
			});

			// Prepend missing options to the top
			let resultItems = items;
			if (missingOptions.length > 0) {
				resultItems = [...missingOptions, ...items];
			}
			
			// Add Select All option at the top if needed
			if (shouldShowSelectAll) {
				return [selectAllOption, ...resultItems];
			}
			return resultItems;
		}

		// Handle single-select case
		if (!isMulti && value) {
			const selectedValue = getOptionValue(value as Option);

			// If items array is empty, just return the value
			if (items.length === 0) {
				return [value as Option];
			}

			const exists = optionExistsInItems(selectedValue, items);

			// If selected value is not in items, add it to the top
			if (!exists) {
				return [value as Option, ...items];
			}
		}

		return shouldShowSelectAll ? [selectAllOption, ...items] : items;
	}, [items, rest.value, isMulti, onLoadMore]);

	const [brandSolid, brandSecondary] = useToken('colors', [
		'brand.solid',
		'brand.solidSecondary',
	]);
	const solidGradiant = `linear-gradient(90deg, ${brandSolid} 0%, ${brandSecondary} 100%)`;

	return (
		<Box>
			{topLabel && (
				<Box display="flex" alignItems="center" gap={1}>
					<FaceliftText fontStyle="italic" fontSize="sm">
						{topLabel}
					</FaceliftText>
					{required && <FaceliftText color={colors.errors}>*</FaceliftText>}
				</Box>
			)}

			<Box position="relative">
				<Tooltip
					showArrow
					contentProps={{ css: { '--tooltip-bg': 'colors.brand.errors' } }}
					color="brand.muted"
					open={!!error}
					content={error}
					positioning={{ placement: 'top-center' }}
					portalled={false}
				>
					<FaceliftSelectInner
						key={selectKey}
						items={enhancedItems}
						isMulti={isMulti}
						loading={loading}
						solidGradiant={solidGradiant}
						placeholder={placeholder}
						debouncedSearch={debouncedSearch}
						onScrollToBottom={onLoadMore ? onScrollToBottom : null}
						error={error}
						color={color}
						ai={ai}
						rounded={rounded}
						{...rest}
					/>
				</Tooltip>
			</Box>

			<Show when={ai}>
				<Box
					boxShadow={`0px 0px 10px 0px ${colors.aiBoxShadow}`}
					borderRadius={4}
					mt={3}
					px={4}
					py={2}
				>
					<FaceliftText
						fontSize="xs"
						fontWeight={700}
						display="flex"
						alignItems="center"
						gap={1}
					>
						<BulbIcon />
						AI Suggestions
					</FaceliftText>
					<FaceliftText fontSize="xs" fontWeight={400}>
						{aiSuggestionText}
					</FaceliftText>
				</Box>
			</Show>
		</Box>
	);
};

export default FaceliftSelect;
